import Navbar from '../components/Navbar';
import { useLanguage } from '../contexts/LanguageContext';
import { Calendar, Video, CreditCard, Globe, Shield, Languages } from 'lucide-react';
import { Button } from '../components/ui/button';

const PracticalInfoPage = () => {
  const { language } = useLanguage();

  // Contenu des sections par langue
  const content = {
    fr: {
      pageTitle: 'Informations pratiques',
      pageSubtitle: "Tout ce que vous devez savoir pour consulter un praticien à l'étranger",
      sections: [
        {
          icon: Calendar,
          title: 'Comment réserver un rendez-vous ?',
          items: [
            "1. Recherchez un praticien depuis l'annuaire (filtres par pays, région, ville et spécialité).",
            "2. Cliquez sur sa fiche pour voir ses tarifs et disponibilités.",
            "3. Choisissez le type de consultation : en cabinet ou téléconsultation vidéo.",
            "4. Sélectionnez un créneau et procédez au paiement sécurisé Stripe.",
            "5. Vous recevrez un email de confirmation, puis un rappel automatique 24h avant le RDV."
          ]
        },
        {
          icon: Video,
          title: 'Comment fonctionne la téléconsultation ?',
          items: [
            "Lors de la prise de rendez-vous, choisissez l'option « Visio » et payez en ligne.",
            "Un lien de visioconférence sécurisé (Jitsi) est généré automatiquement et inclus dans votre email de confirmation.",
            "Le jour du RDV, cliquez sur le lien depuis votre ordinateur, smartphone ou tablette : aucune installation n'est requise.",
            "La téléconsultation est idéale pour un avis rapide, un suivi, ou une question de bien-être."
          ]
        },
        {
          icon: CreditCard,
          title: 'Tarifs et paiements',
          items: [
            "Le tarif de la consultation est fixé librement par chaque praticien (visible avant la réservation).",
            "100% du tarif consultation est reversé directement au praticien — la plateforme ne prélève aucune commission par RDV.",
            "Les praticiens bénéficient de 5 mois d'essai gratuits puis un abonnement de 50€/mois.",
            "Les frais d'inscription unique sont de 50€ (collectés au moment de la création du compte praticien).",
            "Tous les paiements transitent par Stripe (PCI-DSS niveau 1)."
          ]
        },
        {
          icon: Globe,
          title: 'Patients internationaux',
          items: [
            "Trouvez des praticiens dans le pays de votre choix grâce à nos filtres de recherche.",
            "Pour les soins courts, un visa touristique suffit généralement. Pour des traitements plus longs, consultez l'ambassade.",
            "Les paiements sont sécurisés et acceptent les principales cartes bancaires internationales.",
            "Les tarifs sont affichés en euros. Votre banque peut appliquer des frais de change."
          ]
        },
        {
          icon: Shield,
          title: 'Assurance santé',
          items: [
            "La couverture dépend de votre contrat d'assurance.",
            "Nous vous recommandons de contacter votre assureur avant votre consultation.",
            "Certaines mutuelles internationales couvrent les médecines alternatives et le bien-être.",
            "Conservez vos factures pour d'éventuels remboursements."
          ]
        },
        {
          icon: Languages,
          title: 'Langues parlées',
          items: [
            "Beaucoup de nos praticiens sont multilingues.",
            "Vous pouvez voir les langues parlées sur chaque profil de praticien.",
            "Utilisez le filtre de langue pour trouver un praticien qui parle votre langue.",
            "La plateforme est disponible en français, anglais, allemand, italien, russe, roumain, ukrainien et géorgien."
          ]
        }
      ],
      ctaTitle: "Vous avez d'autres questions ?",
      ctaSubtitle: "Contactez notre équipe de support",
      email: "contact@apora.net"
    },
    en: {
      pageTitle: 'Practical Information',
      pageSubtitle: 'Everything you need to know to consult a practitioner abroad',
      sections: [
        {
          icon: Calendar,
          title: 'How to book an appointment?',
          items: [
            "1. Search for a practitioner from the directory (filters by country, region, city and specialty).",
            "2. Click on their profile to see their rates and availability.",
            "3. Choose the type of consultation: in-office or video teleconsultation.",
            "4. Select a time slot and proceed with secure Stripe payment.",
            "5. You will receive a confirmation email, then an automatic reminder 24h before the appointment."
          ]
        },
        {
          icon: Video,
          title: 'How does teleconsultation work?',
          items: [
            "When booking, choose the 'Video' option and pay online.",
            "A secure video conference link (Jitsi) is automatically generated and included in your confirmation email.",
            "On the day of the appointment, click the link from your computer, smartphone or tablet: no installation required.",
            "Teleconsultation is ideal for quick advice, follow-up, or wellness questions."
          ]
        },
        {
          icon: CreditCard,
          title: 'Rates and payments',
          items: [
            "The consultation fee is set freely by each practitioner (visible before booking).",
            "100% of the consultation fee goes directly to the practitioner — the platform takes no commission per appointment.",
            "Practitioners benefit from 5 months free trial then a subscription of €50/month.",
            "The one-time registration fee is €50 (collected when creating the practitioner account).",
            "All payments go through Stripe (PCI-DSS level 1)."
          ]
        },
        {
          icon: Globe,
          title: 'International patients',
          items: [
            "Find practitioners in your country of choice using our search filters.",
            "For short treatments, a tourist visa is usually sufficient. For longer treatments, consult the embassy.",
            "Payments are secure and accept major international credit cards.",
            "Prices are displayed in euros. Your bank may apply exchange fees."
          ]
        },
        {
          icon: Shield,
          title: 'Health insurance',
          items: [
            "Coverage depends on your insurance contract.",
            "We recommend contacting your insurer before your consultation.",
            "Some international health plans cover alternative medicine and wellness.",
            "Keep your invoices for possible reimbursements."
          ]
        },
        {
          icon: Languages,
          title: 'Languages spoken',
          items: [
            "Many of our practitioners are multilingual.",
            "You can see the languages spoken on each practitioner's profile.",
            "Use the language filter to find a practitioner who speaks your language.",
            "The platform is available in French, English, German, Italian, Russian, Romanian, Ukrainian and Georgian."
          ]
        }
      ],
      ctaTitle: "Still have questions?",
      ctaSubtitle: "Contact our support team",
      email: "contact@apora.net"
    },
    de: {
      pageTitle: 'Praktische Informationen',
      pageSubtitle: 'Alles, was Sie wissen müssen, um einen Praktiker im Ausland zu konsultieren',
      sections: [
        {
          icon: Calendar,
          title: 'Wie buche ich einen Termin?',
          items: [
            "1. Suchen Sie einen Praktiker im Verzeichnis (Filter nach Land, Region, Stadt und Fachgebiet).",
            "2. Klicken Sie auf sein Profil, um seine Preise und Verfügbarkeit zu sehen.",
            "3. Wählen Sie die Art der Beratung: vor Ort oder Videokonsultation.",
            "4. Wählen Sie einen Zeitslot und zahlen Sie sicher über Stripe.",
            "5. Sie erhalten eine Bestätigungs-E-Mail und eine automatische Erinnerung 24h vor dem Termin."
          ]
        },
        {
          icon: Video,
          title: 'Wie funktioniert die Telekonsultation?',
          items: [
            "Bei der Buchung wählen Sie die Option 'Video' und zahlen online.",
            "Ein sicherer Videokonferenz-Link (Jitsi) wird automatisch generiert und in Ihrer Bestätigungs-E-Mail enthalten.",
            "Am Tag des Termins klicken Sie auf den Link von Ihrem Computer, Smartphone oder Tablet: keine Installation erforderlich.",
            "Telekonsultation ist ideal für schnelle Beratung, Nachsorge oder Wellness-Fragen."
          ]
        },
        {
          icon: CreditCard,
          title: 'Preise und Zahlungen',
          items: [
            "Die Beratungsgebühr wird von jedem Praktiker frei festgelegt (vor der Buchung sichtbar).",
            "100% der Beratungsgebühr geht direkt an den Praktiker — die Plattform nimmt keine Provision pro Termin.",
            "Praktiker profitieren von 5 Monaten kostenloser Testphase, dann ein Abonnement von 50€/Monat.",
            "Die einmalige Registrierungsgebühr beträgt 50€ (bei der Erstellung des Praktikerkontos erhoben).",
            "Alle Zahlungen laufen über Stripe (PCI-DSS Level 1)."
          ]
        },
        {
          icon: Globe,
          title: 'Internationale Patienten',
          items: [
            "Finden Sie Praktiker in Ihrem Wunschland mit unseren Suchfiltern.",
            "Für kurze Behandlungen reicht in der Regel ein Touristenvisum. Für längere Behandlungen wenden Sie sich an die Botschaft.",
            "Zahlungen sind sicher und akzeptieren wichtige internationale Kreditkarten.",
            "Preise werden in Euro angezeigt. Ihre Bank kann Wechselgebühren berechnen."
          ]
        },
        {
          icon: Shield,
          title: 'Krankenversicherung',
          items: [
            "Die Deckung hängt von Ihrem Versicherungsvertrag ab.",
            "Wir empfehlen, vor Ihrer Beratung Ihren Versicherer zu kontaktieren.",
            "Einige internationale Krankenversicherungen decken alternative Medizin und Wellness ab.",
            "Bewahren Sie Ihre Rechnungen für mögliche Erstattungen auf."
          ]
        },
        {
          icon: Languages,
          title: 'Gesprochene Sprachen',
          items: [
            "Viele unserer Praktiker sind mehrsprachig.",
            "Sie können die gesprochenen Sprachen auf dem Profil jedes Praktikers sehen.",
            "Verwenden Sie den Sprachfilter, um einen Praktiker zu finden, der Ihre Sprache spricht.",
            "Die Plattform ist verfügbar auf Französisch, Englisch, Deutsch, Italienisch, Russisch, Rumänisch, Ukrainisch und Georgisch."
          ]
        }
      ],
      ctaTitle: "Haben Sie noch Fragen?",
      ctaSubtitle: "Kontaktieren Sie unser Support-Team",
      email: "contact@apora.net"
    },
    ru: {
      pageTitle: 'Практическая информация',
      pageSubtitle: 'Всё, что вам нужно знать для консультации со специалистом за рубежом',
      sections: [
        {
          icon: Calendar,
          title: 'Как записаться на приём?',
          items: [
            "1. Найдите специалиста в каталоге (фильтры по стране, региону, городу и специальности).",
            "2. Нажмите на профиль, чтобы увидеть тарифы и доступность.",
            "3. Выберите тип консультации: в кабинете или видеоконсультация.",
            "4. Выберите время и оплатите безопасно через Stripe.",
            "5. Вы получите email-подтверждение и автоматическое напоминание за 24ч до приёма."
          ]
        },
        {
          icon: Video,
          title: 'Как работает телеконсультация?',
          items: [
            "При записи выберите опцию 'Видео' и оплатите онлайн.",
            "Защищённая ссылка на видеоконференцию (Jitsi) автоматически генерируется и включается в email-подтверждение.",
            "В день приёма нажмите на ссылку с компьютера, смартфона или планшета: установка не требуется.",
            "Телеконсультация идеальна для быстрого совета, наблюдения или вопросов о здоровье."
          ]
        },
        {
          icon: CreditCard,
          title: 'Тарифы и оплата',
          items: [
            "Стоимость консультации устанавливается каждым специалистом свободно (видна до бронирования).",
            "100% стоимости консультации идёт напрямую специалисту — платформа не берёт комиссию за приём.",
            "Специалисты получают 5 месяцев бесплатного пробного периода, затем подписка 50€/месяц.",
            "Единовременный регистрационный взнос составляет 50€ (взимается при создании аккаунта специалиста).",
            "Все платежи проходят через Stripe (PCI-DSS уровень 1)."
          ]
        },
        {
          icon: Globe,
          title: 'Международные пациенты',
          items: [
            "Найдите специалистов в выбранной стране с помощью наших фильтров поиска.",
            "Для краткосрочного лечения обычно достаточно туристической визы. Для длительного — обратитесь в посольство.",
            "Платежи защищены и принимают основные международные кредитные карты.",
            "Цены указаны в евро. Ваш банк может применить комиссию за обмен валюты."
          ]
        },
        {
          icon: Shield,
          title: 'Медицинская страховка',
          items: [
            "Покрытие зависит от вашего страхового договора.",
            "Рекомендуем связаться со страховщиком перед консультацией.",
            "Некоторые международные планы покрывают альтернативную медицину и велнес.",
            "Сохраняйте счета для возможного возмещения."
          ]
        },
        {
          icon: Languages,
          title: 'Языки общения',
          items: [
            "Многие наши специалисты многоязычны.",
            "Вы можете увидеть языки на профиле каждого специалиста.",
            "Используйте фильтр языка, чтобы найти специалиста, говорящего на вашем языке.",
            "Платформа доступна на французском, английском, немецком, итальянском, русском, румынском, украинском и грузинском."
          ]
        }
      ],
      ctaTitle: "Остались вопросы?",
      ctaSubtitle: "Свяжитесь с нашей командой поддержки",
      email: "contact@apora.net"
    },
    uk: {
      pageTitle: 'Практична інформація',
      pageSubtitle: 'Все, що вам потрібно знати для консультації зі спеціалістом за кордоном',
      sections: [
        {
          icon: Calendar,
          title: 'Як записатися на прийом?',
          items: [
            "1. Знайдіть спеціаліста в каталозі (фільтри за країною, регіоном, містом та спеціальністю).",
            "2. Натисніть на профіль, щоб побачити тарифи та доступність.",
            "3. Виберіть тип консультації: в кабінеті або відеоконсультація.",
            "4. Виберіть час та оплатіть безпечно через Stripe.",
            "5. Ви отримаєте email-підтвердження та автоматичне нагадування за 24 год до прийому."
          ]
        },
        {
          icon: Video,
          title: 'Як працює телеконсультація?',
          items: [
            "При записі виберіть опцію 'Відео' та оплатіть онлайн.",
            "Захищене посилання на відеоконференцію (Jitsi) автоматично генерується та включається в email-підтвердження.",
            "В день прийому натисніть на посилання з комп'ютера, смартфона або планшета: встановлення не потрібне.",
            "Телеконсультація ідеальна для швидкої поради, спостереження або питань про здоров'я."
          ]
        },
        {
          icon: CreditCard,
          title: 'Тарифи та оплата',
          items: [
            "Вартість консультації встановлюється кожним спеціалістом вільно (видна до бронювання).",
            "100% вартості консультації йде напряму спеціалісту — платформа не бере комісію за прийом.",
            "Спеціалісти отримують 5 місяців безкоштовного пробного періоду, потім підписка 50€/місяць.",
            "Одноразовий реєстраційний внесок становить 50€ (стягується при створенні акаунту спеціаліста).",
            "Всі платежі проходять через Stripe (PCI-DSS рівень 1)."
          ]
        },
        {
          icon: Globe,
          title: 'Міжнародні пацієнти',
          items: [
            "Знайдіть спеціалістів у вибраній країні за допомогою наших фільтрів пошуку.",
            "Для короткострокового лікування зазвичай достатньо туристичної візи. Для тривалого — зверніться до посольства.",
            "Платежі захищені та приймають основні міжнародні кредитні картки.",
            "Ціни вказані в євро. Ваш банк може застосувати комісію за обмін валюти."
          ]
        },
        {
          icon: Shield,
          title: 'Медичне страхування',
          items: [
            "Покриття залежить від вашого страхового договору.",
            "Рекомендуємо зв'язатися зі страховиком перед консультацією.",
            "Деякі міжнародні плани покривають альтернативну медицину та велнес.",
            "Зберігайте рахунки для можливого відшкодування."
          ]
        },
        {
          icon: Languages,
          title: 'Мови спілкування',
          items: [
            "Багато наших спеціалістів багатомовні.",
            "Ви можете побачити мови на профілі кожного спеціаліста.",
            "Використовуйте фільтр мови, щоб знайти спеціаліста, який говорить вашою мовою.",
            "Платформа доступна французькою, англійською, німецькою, італійською, російською, румунською, українською та грузинською."
          ]
        }
      ],
      ctaTitle: "Залишилися питання?",
      ctaSubtitle: "Зв'яжіться з нашою командою підтримки",
      email: "contact@apora.net"
    },
    ka: {
      pageTitle: 'პრაქტიკული ინფორმაცია',
      pageSubtitle: 'ყველაფერი რაც უნდა იცოდეთ საზღვარგარეთ სპეციალისტთან კონსულტაციისთვის',
      sections: [
        {
          icon: Calendar,
          title: 'როგორ დავჯავშნო შეხვედრა?',
          items: [
            "1. მოძებნეთ სპეციალისტი კატალოგში (ფილტრები ქვეყნის, რეგიონის, ქალაქისა და სპეციალობის მიხედვით).",
            "2. დააწკაპუნეთ პროფილზე ტარიფებისა და ხელმისაწვდომობის სანახავად.",
            "3. აირჩიეთ კონსულტაციის ტიპი: კაბინეტში ან ვიდეოკონსულტაცია.",
            "4. აირჩიეთ დრო და გადაიხადეთ უსაფრთხოდ Stripe-ის მეშვეობით.",
            "5. მიიღებთ email-დადასტურებას და ავტომატურ შეხსენებას 24 საათით ადრე."
          ]
        },
        {
          icon: Video,
          title: 'როგორ მუშაობს ტელეკონსულტაცია?',
          items: [
            "ჯავშნისას აირჩიეთ 'ვიდეო' ოფცია და გადაიხადეთ ონლაინ.",
            "დაცული ვიდეოკონფერენციის ბმული (Jitsi) ავტომატურად იქმნება და შედის email-დადასტურებაში.",
            "შეხვედრის დღეს დააწკაპუნეთ ბმულზე კომპიუტერიდან, სმარტფონიდან ან ტაბლეტიდან: ინსტალაცია არ არის საჭირო.",
            "ტელეკონსულტაცია იდეალურია სწრაფი რჩევისთვის, თვალყურის დევნებისთვის ან ჯანმრთელობის კითხვებისთვის."
          ]
        },
        {
          icon: CreditCard,
          title: 'ტარიფები და გადახდა',
          items: [
            "კონსულტაციის ღირებულება თავისუფლად დგინდება თითოეული სპეციალისტის მიერ (ჩანს დაჯავშნამდე).",
            "კონსულტაციის ღირებულების 100% მიდის პირდაპირ სპეციალისტთან — პლატფორმა არ იღებს საკომისიოს.",
            "სპეციალისტები იღებენ 5 თვიან უფასო საცდელ პერიოდს, შემდეგ გამოწერა 50€/თვეში.",
            "ერთჯერადი რეგისტრაციის საფასური არის 50€ (გადახდა სპეციალისტის ანგარიშის შექმნისას).",
            "ყველა გადახდა გადის Stripe-ის მეშვეობით (PCI-DSS დონე 1)."
          ]
        },
        {
          icon: Globe,
          title: 'საერთაშორისო პაციენტები',
          items: [
            "იპოვეთ სპეციალისტები არჩეულ ქვეყანაში ჩვენი ძიების ფილტრების გამოყენებით.",
            "მოკლევადიანი მკურნალობისთვის ჩვეულებრივ საკმარისია ტურისტული ვიზა. ხანგრძლივისთვის — მიმართეთ საელჩოს.",
            "გადახდები დაცულია და მიიღება ძირითადი საერთაშორისო საკრედიტო ბარათები.",
            "ფასები მითითებულია ევროში. თქვენმა ბანკმა შეიძლება გამოიყენოს კონვერტაციის საკომისიო."
          ]
        },
        {
          icon: Shield,
          title: 'ჯანმრთელობის დაზღვევა',
          items: [
            "დაფარვა დამოკიდებულია თქვენს სადაზღვევო ხელშეკრულებაზე.",
            "გირჩევთ დაუკავშირდეთ თქვენს დამზღვევს კონსულტაციამდე.",
            "ზოგიერთი საერთაშორისო გეგმა ფარავს ალტერნატიულ მედიცინას და ველნესს.",
            "შეინახეთ ინვოისები შესაძლო ანაზღაურებისთვის."
          ]
        },
        {
          icon: Languages,
          title: 'საუბრის ენები',
          items: [
            "ჩვენი სპეციალისტების უმრავლესობა მრავალენოვანია.",
            "შეგიძლიათ ნახოთ ენები თითოეული სპეციალისტის პროფილზე.",
            "გამოიყენეთ ენის ფილტრი თქვენს ენაზე მოსაუბრე სპეციალისტის მოსაძებნად.",
            "პლატფორმა ხელმისაწვდომია ფრანგულად, ინგლისურად, გერმანულად, იტალიურად, რუსულად, რუმინულად, უკრაინულად და ქართულად."
          ]
        }
      ],
      ctaTitle: "კიდევ გაქვთ კითხვები?",
      ctaSubtitle: "დაუკავშირდით ჩვენს მხარდაჭერის გუნდს",
      email: "contact@apora.net"
    },
    it: {
      pageTitle: 'Informazioni pratiche',
      pageSubtitle: "Tutto ciò che devi sapere per consultare un professionista all'estero",
      sections: [
        {
          icon: Calendar,
          title: 'Come prenotare un appuntamento?',
          items: [
            "1. Cerca un professionista nell'elenco (filtri per paese, regione, città e specialità).",
            "2. Clicca sul profilo per vedere le tariffe e la disponibilità.",
            "3. Scegli il tipo di consulenza: in studio o videoconsulenza.",
            "4. Seleziona un orario e paga in sicurezza tramite Stripe.",
            "5. Riceverai un'email di conferma, poi un promemoria automatico 24h prima dell'appuntamento."
          ]
        },
        {
          icon: Video,
          title: 'Come funziona la teleconsulenza?',
          items: [
            "Alla prenotazione, scegli l'opzione 'Video' e paga online.",
            "Un link sicuro per la videoconferenza (Jitsi) viene generato automaticamente e incluso nell'email di conferma.",
            "Il giorno dell'appuntamento, clicca sul link dal computer, smartphone o tablet: nessuna installazione richiesta.",
            "La teleconsulenza è ideale per un consiglio rapido, follow-up o domande sul benessere."
          ]
        },
        {
          icon: CreditCard,
          title: 'Tariffe e pagamenti',
          items: [
            "La tariffa della consulenza è stabilita liberamente da ogni professionista (visibile prima della prenotazione).",
            "Il 100% della tariffa va direttamente al professionista — la piattaforma non prende commissioni per appuntamento.",
            "I professionisti beneficiano di 5 mesi di prova gratuita, poi un abbonamento di 50€/mese.",
            "La quota di iscrizione una tantum è di 50€ (raccolta alla creazione dell'account professionista).",
            "Tutti i pagamenti passano attraverso Stripe (PCI-DSS livello 1)."
          ]
        },
        {
          icon: Globe,
          title: 'Pazienti internazionali',
          items: [
            "Trova professionisti nel paese di tua scelta usando i nostri filtri di ricerca.",
            "Per trattamenti brevi, un visto turistico è solitamente sufficiente. Per trattamenti più lunghi, consulta l'ambasciata.",
            "I pagamenti sono sicuri e accettano le principali carte di credito internazionali.",
            "I prezzi sono indicati in euro. La tua banca potrebbe applicare commissioni di cambio."
          ]
        },
        {
          icon: Shield,
          title: 'Assicurazione sanitaria',
          items: [
            "La copertura dipende dal tuo contratto assicurativo.",
            "Ti consigliamo di contattare il tuo assicuratore prima della consulenza.",
            "Alcune assicurazioni internazionali coprono la medicina alternativa e il benessere.",
            "Conserva le fatture per eventuali rimborsi."
          ]
        },
        {
          icon: Languages,
          title: 'Lingue parlate',
          items: [
            "Molti dei nostri professionisti sono multilingue.",
            "Puoi vedere le lingue parlate sul profilo di ogni professionista.",
            "Usa il filtro della lingua per trovare un professionista che parla la tua lingua.",
            "La piattaforma è disponibile in francese, inglese, tedesco, italiano, russo, rumeno, ucraino e georgiano."
          ]
        }
      ],
      ctaTitle: "Hai altre domande?",
      ctaSubtitle: "Contatta il nostro team di supporto",
      email: "contact@apora.net"
    },
    ro: {
      pageTitle: 'Informații practice',
      pageSubtitle: 'Tot ce trebuie să știi pentru a consulta un specialist în străinătate',
      sections: [
        {
          icon: Calendar,
          title: 'Cum rezerv o programare?',
          items: [
            "1. Caută un specialist în catalog (filtre după țară, regiune, oraș și specialitate).",
            "2. Dă clic pe profil pentru a vedea tarifele și disponibilitatea.",
            "3. Alege tipul de consultație: la cabinet sau videoconsultație.",
            "4. Selectează un interval orar și plătește securizat prin Stripe.",
            "5. Vei primi un email de confirmare, apoi o reamintire automată cu 24h înainte de programare."
          ]
        },
        {
          icon: Video,
          title: 'Cum funcționează teleconsultația?',
          items: [
            "La rezervare, alege opțiunea 'Video' și plătește online.",
            "Un link securizat pentru videoconferință (Jitsi) este generat automat și inclus în emailul de confirmare.",
            "În ziua programării, dă clic pe link de pe computer, smartphone sau tabletă: nu e nevoie de instalare.",
            "Teleconsultația e ideală pentru sfaturi rapide, monitorizare sau întrebări despre sănătate."
          ]
        },
        {
          icon: CreditCard,
          title: 'Tarife și plăți',
          items: [
            "Tariful consultației este stabilit liber de fiecare specialist (vizibil înainte de rezervare).",
            "100% din tariful consultației merge direct la specialist — platforma nu ia comision pe programare.",
            "Specialiștii beneficiază de 5 luni de probă gratuită, apoi un abonament de 50€/lună.",
            "Taxa de înregistrare unică este de 50€ (colectată la crearea contului de specialist).",
            "Toate plățile trec prin Stripe (PCI-DSS nivel 1)."
          ]
        },
        {
          icon: Globe,
          title: 'Pacienți internaționali',
          items: [
            "Găsește specialiști în țara dorită folosind filtrele noastre de căutare.",
            "Pentru tratamente scurte, o viză turistică e de obicei suficientă. Pentru tratamente mai lungi, consultă ambasada.",
            "Plățile sunt securizate și acceptă principalele carduri de credit internaționale.",
            "Prețurile sunt afișate în euro. Banca ta poate aplica comisioane de schimb valutar."
          ]
        },
        {
          icon: Shield,
          title: 'Asigurare de sănătate',
          items: [
            "Acoperirea depinde de contractul tău de asigurare.",
            "Îți recomandăm să contactezi asigurătorul înainte de consultație.",
            "Unele asigurări internaționale acoperă medicina alternativă și wellness-ul.",
            "Păstrează facturile pentru eventuale rambursări."
          ]
        },
        {
          icon: Languages,
          title: 'Limbi vorbite',
          items: [
            "Mulți dintre specialiștii noștri sunt multilingvi.",
            "Poți vedea limbile vorbite pe profilul fiecărui specialist.",
            "Folosește filtrul de limbă pentru a găsi un specialist care vorbește limba ta.",
            "Platforma este disponibilă în franceză, engleză, germană, italiană, rusă, română, ucraineană și georgiană."
          ]
        }
      ],
      ctaTitle: "Ai alte întrebări?",
      ctaSubtitle: "Contactează echipa noastră de suport",
      email: "contact@apora.net"
    }
  };

  // Fallback to French if language not found
  const currentContent = content[language] || content['fr'];

  return (
    <div className="min-h-screen bg-background" data-testid="practical-info-page">
      <Navbar />
      
      {/* Hero Section */}
      <div className="bg-gradient-to-br from-sage-50 to-sand-50 py-16">
        <div className="container-app">
          <div className="max-w-3xl mx-auto text-center">
            <h1 className="font-heading text-4xl md:text-5xl font-bold text-foreground mb-4">
              {currentContent.pageTitle}
            </h1>
            <p className="text-lg text-muted-foreground">
              {currentContent.pageSubtitle}
            </p>
          </div>
        </div>
      </div>

      {/* Info Sections - Style MyVeto */}
      <div className="container-app py-12">
        <div className="max-w-4xl mx-auto space-y-6">
          {currentContent.sections.map((section, index) => (
            <div 
              key={index}
              className="bg-white rounded-2xl border border-sand-200 shadow-soft p-6 md:p-8"
            >
              <div className="flex items-start gap-4 mb-4">
                <div className="w-12 h-12 rounded-xl bg-sage-100 flex items-center justify-center shrink-0">
                  <section.icon className="w-6 h-6 text-sage-600" />
                </div>
                <h2 className="font-heading text-xl md:text-2xl font-semibold text-foreground pt-2">
                  {section.title}
                </h2>
              </div>
              
              <div className="pl-0 md:pl-16 space-y-3">
                {section.items.map((item, itemIndex) => (
                  <p key={itemIndex} className="text-muted-foreground leading-relaxed">
                    {item}
                  </p>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Contact CTA */}
      <div className="bg-sage-500 py-12">
        <div className="container-app text-center">
          <h2 className="font-heading text-2xl font-bold text-white mb-4">
            {currentContent.ctaTitle}
          </h2>
          <p className="text-sage-100 mb-6">
            {currentContent.ctaSubtitle}
          </p>
          <Button 
            asChild
            className="bg-white text-sage-600 hover:bg-sand-50"
          >
            <a href={`mailto:${currentContent.email}`}>{currentContent.email}</a>
          </Button>
        </div>
      </div>
    </div>
  );
};

export default PracticalInfoPage;
